import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Buyer,
  DocumentsSummary,
  QuickbooksInvoice,
  RemittanceRecord,
  SupplierForBuyer,
} from '@dill/dill-shared';
import {
  canUserAccessBuyerBranch,
  ERP_POSTING_NOTE_MAX_LENGTH,
  isRecordSettledForPosting,
} from '@dill/dill-shared';
import { AllocationEditor } from '../components/AllocationEditor';
import { DillModal, DillButton, DillSelect } from '../../../general/Dill';
import {
  CheckCircle,
  FloppyDisk,
  File,
  ArrowSquareOut,
  Info,
  UserPlus,
  LinkBreak,
  Clock,
} from 'phosphor-react';
import { ChannelBadge } from '../components/ChannelBadge';
import { ConfidenceBadge } from '../components/ConfidenceBadge';
import { ConfidenceFactorList } from '../components/ConfidenceFactorList';
import { formatCurrency, formatDate, formatDateTime } from '../utils';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  assignRemittanceBuyerThunk,
  unlinkBankTransactionThunk,
  updateRemittanceNoteThunk,
} from '../../../../redux/services/cashApplicationService';
import { openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../redux/services/suppliersService';
import { getGroupedSuppliersInvoices } from '../../../../redux/services/invoicesService';
import { openDillToast } from '../../../../utils/helpers';

interface AdjustAllocationModalProps {
  adjustItem: RemittanceRecord | null;
  allocations: Record<string, number>;
  onAllocationChange: (invoiceId: string, value: string) => void;
  totalAllocated: number;
  filteredOpenInvoices: QuickbooksInvoice[];
  invoiceSearch: string;
  onInvoiceSearchChange: (value: string) => void;
  onClose: () => void;
  onSaveOnly: () => void;
  /**
   * Omitted where the caller has no authority to post — the Awaiting Bank queue, whose records
   * post themselves on settlement and whose one manual posting route is a separate confirmed
   * action. Leaving the button out is the enforcement; there is no disabled state to explain.
   */
  onAdjustAndPost?: () => void;
  onPostWithoutApply?: () => void;
  isValidAllocation: boolean;
  hasAllocations: boolean;
  approveLabel?: string;
  saveLabel?: string;
  /** Replaces the modal title where "Review & Adjust" would imply a posting decision. */
  title?: string;
  /** Passed through to AllocationEditor; see onPostRemainderChange there. */
  onPostRemainderChange?: (value: boolean) => void;
  postRemainderAsCredit?: boolean;
  /**
   * Drop every allocation the editor is holding, because they belong to a customer this record no
   * longer names.
   *
   * The allocations are the caller's state, so only the caller can clear them. Omitting this leaves
   * the customer read-only — a caller that cannot clear must not offer a change it cannot make
   * safe. See `performCustomerAssignment`.
   */
  onAllocationsReset?: () => void;
}

export const AdjustAllocationModal = ({
  adjustItem,
  allocations,
  onAllocationChange,
  totalAllocated,
  filteredOpenInvoices,
  invoiceSearch,
  onInvoiceSearchChange,
  onClose,
  onSaveOnly,
  onAdjustAndPost,
  onPostWithoutApply,
  isValidAllocation,
  hasAllocations,
  approveLabel = 'Approve & Post',
  saveLabel = 'Save',
  title = 'Review & Adjust Allocation',
  onPostRemainderChange,
  postRemainderAsCredit,
  onAllocationsReset,
}: AdjustAllocationModalProps) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const user = useAppSelector((state) => state.auth.user);
  const { customers } = useAppSelector((state) => state.supplier);
  const bankTransactions = useAppSelector((state) => state.cashApplication.bankTransactions);
  const supplierId = currentSupplier?.id;

  // Same supplier-switch guards the other customer pickers use: a chain started for a previous
  // supplier must not write its results into a now differently-scoped store.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightCustomersRequestRef.current?.abort(), [currentSupplier?.id]);

  const [customerCount, setCustomerCount] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [assigning, setAssigning] = useState(false);
  /**
   * Whether the customer picker is open on a record that already has one.
   *
   * A customer was shown as plain text once it existed, so the only way to correct one was to leave
   * the modal. That assumed the matcher is either right or silent, and it is neither: a head-word
   * match scores 0.60 against a threshold of 0.50, so "Acme Widgets" can be booked to "ACME
   * Construction" with nothing on screen inviting a second opinion. Editing the allocation is
   * exactly where someone notices the invoices belong to another company.
   *
   * Held open explicitly rather than showing the picker always, so the ordinary case reads as a
   * fact about the record rather than a question being asked of the operator.
   */
  const [changingCustomer, setChangingCustomer] = useState(false);
  /**
   * The customer picked but not yet applied.
   *
   * Selecting used to be the commit: choosing a name from the list immediately reassigned the
   * payment, so a mis-click in a list of several hundred was a decision already taken, and there
   * was no state in which the operator could see their choice and check it before it counted. This
   * holds the choice until they say so, which is also what gives the picker a value to display —
   * it was rendered permanently empty because no selection was ever kept.
   */
  const [pendingBuyerId, setPendingBuyerId] = useState('');
  /**
   * The open ledger for whichever customer this record belongs to.
   *
   * Null until it has been asked for, which is what tells `editableInvoices` to fall back to the
   * parent's list. Fetched on open for a record that has a customer, and again when one is assigned
   * here — the parent's list was computed before that customer existed.
   */
  const [assignedInvoices, setAssignedInvoices] = useState<QuickbooksInvoice[] | null>(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  /**
   * The record whose ledger the in-flight fetch belongs to.
   *
   * Guarded by a ref rather than trusted to arrive in order. Stepping through the queue starts a
   * request per record, and without this a slow earlier response lands after a faster later one and
   * the modal shows the previous customer's invoices — under the current customer's name, which is
   * the kind of wrong that gets cash applied to someone else's account.
   */
  const activeInvoiceFetchRef = useRef<string | null>(null);
  /**
   * The customer assigned during this session.
   *
   * Two of the three queues hold the open record as a `useState` snapshot taken on row click, so
   * the store update behind an assignment never reaches this modal. Tracked here so the UI
   * responds immediately; the record itself is already correct, which is what posting reads.
   */
  const [assignedBuyer, setAssignedBuyer] = useState<{ id: string; name: string | null } | null>(
    null
  );

  /**
   * Set once the deposit has been unlinked in this session.
   *
   * Same reason as `assignedBuyer`: two of the three queues hold the open record as a snapshot, so
   * the store update behind an unlink never reaches this modal.
   */
  const [unlinkedDeposit, setUnlinkedDeposit] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  /**
   * The operator's note, as typed and as last saved.
   *
   * Two copies for the same reason `assignedBuyer` above keeps one: two of the three queues pass
   * `adjustItem` as a snapshot taken on row click, so the store update behind a save never reaches
   * this modal and the prop would keep showing the old note.
   *
   * Saved explicitly rather than on blur. It is free text, and a request fired every time focus
   * leaves the box would both surprise the writer and race their own typing.
   */
  const [noteDraft, setNoteDraft] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  /**
   * A posted payment's note is already on its receipt and, for an integrated supplier, on a
   * PrivateNote in their ERP. The server refuses to change it; the box is read-only here so the
   * refusal is visible before someone types a paragraph into it.
   */
  const isPosted = adjustItem?.matchStatus === 'APPLIED';

  const hasBuyer = !!adjustItem?.buyerId || !!assignedBuyer;
  const buyerLabel = adjustItem?.buyerId
    ? adjustItem.buyerName ?? adjustItem.buyerId
    : assignedBuyer?.name ?? assignedBuyer?.id ?? '';

  const selectableCustomers = useMemo(
    () =>
      customers.filter((customerDetails) =>
        canUserAccessBuyerBranch({
          user,
          supplierId: currentSupplier?.id,
          buyerBranch: customerDetails?.buyer?.branch,
        })
      ),
    [customers, user, currentSupplier]
  );

  const customerOptions = useMemo(
    () =>
      selectableCustomers
        .map((customerDetails) => ({
          value: customerDetails.buyer.id,
          label: customerDetails.buyer.name || customerDetails.buyer.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [selectableCustomers]
  );

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
        isInvitedUser: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    fetchSupplierId: string
  ) => {
    if (firstCustomers.length > count) {
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      if (!fetchSupplierId || activeFetchSupplierIdRef.current !== fetchSupplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId: fetchSupplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled' && results.payload) {
        const payload = results.payload as any;
        const fetchedCustomers = (payload.data ?? []) as unknown[];
        const lastDocumentId = payload.lastDocumentId;
        if (lastDocumentId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDocumentId);
          currentPreviousLastDocumentId = lastDocumentId;
        }
        if (!lastDockId || fetchedCustomers.length < pageSize) {
          break;
        }
      } else {
        break;
      }
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoadingCustomers(true);
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    try {
      const request = dispatch(
        getUserCustomers({
          supplierId: fetchSupplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
        return;
      }
      if (results.type === 'supplier/getUserCustomers/fulfilled' && results.payload) {
        const payload = results.payload as any;
        const cust = (payload.data ?? []) as {
          buyer: Buyer;
          usersEmailsDetails: {
            email: string;
            isStatementsEmailOptOut: boolean;
            isPaymentRemindersEmailOptOut: boolean;
            isInvitedUser: boolean;
          }[];
          documentsSummary: DocumentsSummary;
        }[];
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);
          const count = Number(payload.customerCount);
          await recursiveFetchCustomers(count, lastDockId, [...cust], fetchSupplierId);
        }
      }
    } finally {
      if (activeFetchSupplierIdRef.current === fetchSupplierId) {
        setLoadingCustomers(false);
      }
    }
  };

  /**
   * The deposit currently vouching for this payment.
   *
   * Shown from the record's side so a wrong link can be undone where it is noticed. The bank
   * record is looked up for its amount and date; the row appears whenever the record carries a
   * `settlementBankRecordId`, even if the deposit itself has not loaded, because a link nobody can
   * see is exactly the one worth being able to break.
   */
  const linkedDepositId = unlinkedDeposit ? null : adjustItem?.settlementBankRecordId ?? null;
  const linkedDeposit = useMemo(
    () => (linkedDepositId ? bankTransactions.find((t) => t.id === linkedDepositId) ?? null : null),
    [linkedDepositId, bankTransactions]
  );

  const handleUnlinkDeposit = useCallback(async () => {
    if (!linkedDepositId || !supplierId || !adjustItem) {
      return;
    }
    setUnlinking(true);
    try {
      const result = await dispatch(
        unlinkBankTransactionThunk({
          supplierId,
          bankTransactionId: linkedDepositId,
          /**
           * This payment only.
           *
           * A deposit can confirm several payments — a file of checks banked as one credit — and
           * the operator is disputing the link in front of them, not the others. Omitting this
           * would detach every payment on the deposit, silently undoing confirmations nobody
           * questioned.
           */
          remittanceId: adjustItem.id,
        })
      );
      if (unlinkBankTransactionThunk.rejected.match(result)) {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to unlink the deposit.',
          type: 'ERROR',
        });
        return;
      }
      setUnlinkedDeposit(true);
      const { stillLinked } = result.payload as { stillLinked: string[] };
      openDillToast({
        message:
          stillLinked.length > 0
            ? `Deposit unlinked from this payment. It still confirms ${stillLinked.length} other payment(s).`
            : 'Deposit unlinked. This payment is awaiting confirmation again.',
        type: 'SUCCESS',
      });
    } finally {
      setUnlinking(false);
    }
  }, [dispatch, linkedDepositId, supplierId, adjustItem]);

  /** Load the customer's open ledger so the operator can allocate against any of it. */
  const fetchBuyerInvoices = async (buyerId: string, forRecordId: string) => {
    if (!buyerId || !supplierId) {
      return;
    }
    activeInvoiceFetchRef.current = forRecordId;
    setLoadingInvoices(true);
    try {
      const results = await dispatch(
        getGroupedSuppliersInvoices({ buyerId, weeksLimit: 12, supplierId })
      );
      if (activeInvoiceFetchRef.current !== forRecordId) {
        return;
      }
      if (results && results.type === 'suppliers/getGroupedSuppliersInvoices/fulfilled') {
        const payload = results.payload as any;
        if (payload?.data?.buyerSuppliers) {
          const data = payload.data.buyerSuppliers as SupplierForBuyer[];
          let invoices: QuickbooksInvoice[] = [];
          data.forEach((item) => {
            invoices = [...invoices, ...(item.invoices?.pastDueInvoices.invoices || [])];
            invoices = [...invoices, ...(item.invoices?.currentInvoices.invoices || [])];
            invoices = [...invoices, ...(item.invoices?.pendingPayInvoices.invoices || [])];
          });
          setAssignedInvoices(invoices.filter((invoice) => (invoice.balance ?? 0) > 0));
          return;
        }
      }
      setAssignedInvoices([]);
    } finally {
      if (activeInvoiceFetchRef.current === forRecordId) {
        setLoadingInvoices(false);
      }
    }
  };

  /**
   * One of two things is needed on open, never both: a customer to pick, or that customer's ledger.
   *
   * The ledger is fetched whether or not the matcher found anything, because "Edit allocation" is
   * exactly where an operator goes to fix a match Dill got wrong — including a payment it matched
   * to nothing. The list used to be restricted to invoices already matched, so a record with a
   * customer and no matches offered nothing to allocate against and the modal could only be
   * cancelled.
   */
  useEffect(() => {
    setAssignedInvoices(null);
    setAssignedBuyer(null);
    setUnlinkedDeposit(false);
    // Stepping to the next record must not leave its customer sitting open for editing, nor carry
    // a selection made for the previous one into the picker.
    setChangingCustomer(false);
    setPendingBuyerId('');
    if (!adjustItem) {
      return;
    }
    if (adjustItem.buyerId) {
      void fetchBuyerInvoices(adjustItem.buyerId, adjustItem.id);
    } else {
      // No customer yet, so there is no ledger to fetch — the operator picks one first, and
      // performCustomerAssignment fetches on the way back.
      activeInvoiceFetchRef.current = null;
      setLoadingInvoices(false);
      handleLoadMore({ isFreshFetch: true });
    }
  }, [adjustItem?.id, adjustItem?.buyerId]);

  // Reseeded per record, not per render: typing must survive the parent re-rendering underneath.
  useEffect(() => {
    const existing = adjustItem?.postingNote ?? '';
    setNoteDraft(existing);
    setSavedNote(existing);
  }, [adjustItem?.id]);

  const handleSaveNote = useCallback(async () => {
    if (!adjustItem || !supplierId) {
      return;
    }
    setSavingNote(true);
    try {
      const next = noteDraft.trim();
      const result = await dispatch(
        updateRemittanceNoteThunk({
          supplierId,
          remittanceId: adjustItem.id,
          postingNote: next.length > 0 ? next : null,
        })
      );
      if (updateRemittanceNoteThunk.rejected.match(result)) {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to save the note.',
          type: 'ERROR',
        });
        return;
      }
      const saved = (result.payload as { postingNote: string | null }).postingNote ?? '';
      setNoteDraft(saved);
      setSavedNote(saved);
      openDillToast({ message: 'Note saved.', type: 'SUCCESS' });
    } finally {
      setSavingNote(false);
    }
  }, [dispatch, adjustItem?.id, supplierId, noteDraft]);

  const performCustomerAssignment = useCallback(
    async (buyerId: string) => {
      if (!adjustItem || !supplierId || !buyerId) {
        return;
      }
      setAssigning(true);
      try {
        const result = await dispatch(
          assignRemittanceBuyerThunk({ supplierId, remittanceId: adjustItem.id, buyerId })
        );
        if (assignRemittanceBuyerThunk.rejected.match(result)) {
          openDillToast({
            message: (result.payload as string) ?? 'Failed to assign the customer.',
            type: 'ERROR',
          });
          return;
        }
        const assigned = result.payload as { buyerId: string; buyerName: string | null };

        /**
         * The allocation does not survive a change of customer.
         *
         * Every line in it points at an invoice in the previous customer's ledger. Carrying them
         * over would apply this cash to another company's invoices — the one error in this modal
         * that posts real money to the wrong account, and the reason the customer could not be
         * edited here before. Cleared rather than remapped: there is no correspondence between two
         * customers' invoices to remap through.
         *
         * Only on an actual change. Re-picking the customer already on the record is a no-op, and
         * wiping a carefully built allocation for it would be its own bug.
         */
        const previousBuyerId = assignedBuyer?.id ?? adjustItem.buyerId ?? null;
        const changed = !!previousBuyerId && previousBuyerId !== assigned.buyerId;

        setAssignedBuyer({ id: assigned.buyerId, name: assigned.buyerName });
        setChangingCustomer(false);
        setPendingBuyerId('');

        if (changed && Object.keys(allocations).length > 0) {
          onAllocationsReset?.();
          openDillToast({
            message: `Customer changed to ${
              assigned.buyerName ?? 'the selected customer'
            }. The previous allocation was cleared — it pointed at the other customer's invoices.`,
            type: 'SUCCESS',
          });
        }

        await fetchBuyerInvoices(buyerId, adjustItem.id);
      } finally {
        setAssigning(false);
      }
    },
    [
      dispatch,
      adjustItem?.id,
      adjustItem?.buyerId,
      supplierId,
      assignedBuyer,
      allocations,
      onAllocationsReset,
    ]
  );

  /** The customer on the record now, whether it arrived with one or was set in this session. */
  const currentBuyerId = assignedBuyer?.id ?? adjustItem?.buyerId ?? null;

  /**
   * Whether the pending selection is worth submitting.
   *
   * Re-picking the customer already on the record is not a change, so the button stays down rather
   * than round-tripping to assign what is already assigned — and, more to the point, rather than
   * clearing an allocation that is still perfectly valid for that customer.
   */
  const canSubmitCustomer = !!pendingBuyerId && pendingBuyerId !== currentBuyerId && !assigning;

  /**
   * Apply the pending selection, asking first when it moves money off an existing customer.
   *
   * Reassigning is not an edit to a field, it is a decision about whose account this payment
   * belongs to, and it discards an allocation built against the previous customer's ledger. The
   * confirmation names both customers and what will be lost, the way unlinking a deposit does.
   */
  const handleSubmitCustomer = useCallback(() => {
    if (!pendingBuyerId || pendingBuyerId === currentBuyerId) {
      return;
    }

    const nextName =
      customerOptions.find((option) => option.value === pendingBuyerId)?.label ??
      'the selected customer';

    // Naming a customer on a record that has none is not destructive and needs no confirming: the
    // submit itself is the deliberate act, and there is no allocation to lose because there was no
    // ledger to build one from.
    if (!currentBuyerId) {
      void performCustomerAssignment(pendingBuyerId);
      return;
    }

    const losingAllocation = Object.keys(allocations).length > 0;

    dispatch(
      openMessageModal({
        modalType: 'WARN',
        title: 'Change this payment’s customer?',
        subTitle:
          `This payment is currently on ${buyerLabel}. Moving it to ${nextName} means the cash ` +
          `will be applied to ${nextName}’s account instead.` +
          (losingAllocation
            ? ` The allocation below will be cleared — those invoices belong to ${buyerLabel}, and there is nothing to carry them over to.`
            : ''),
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Change customer',
        onSecondButtonClick: () => {
          void performCustomerAssignment(pendingBuyerId);
        },
      })
    );
  }, [
    dispatch,
    pendingBuyerId,
    currentBuyerId,
    allocations,
    buyerLabel,
    customerOptions,
    performCustomerAssignment,
  ]);

  /** Abandon the pending selection and put the record's own customer back on screen. */
  const handleCancelCustomerChange = useCallback(() => {
    setPendingBuyerId('');
    setChangingCustomer(false);
  }, []);

  /**
   * Open the picker on a record that already names a customer.
   *
   * The list is fetched here because the load on open is skipped for a record that has a customer —
   * there was nothing to pick, so nothing was fetched. Without this the picker opens empty and
   * reads as broken.
   */
  const handleStartChangeCustomer = useCallback(() => {
    setChangingCustomer(true);
    if (customerOptions.length === 0 && !loadingCustomers) {
      void handleLoadMore({ isFreshFetch: true });
    }
  }, [customerOptions.length, loadingCustomers]);

  /**
   * What the editor allocates against: the customer's whole open ledger, plus whatever was matched.
   *
   * The union is not belt-and-braces. The fetch asks for twelve weeks and keeps only a positive
   * balance, and a matched invoice can be outside both — an older one, or one a prior payment has
   * since closed. `AllocationEditor` renders only invoices it is given, so leaving it out would
   * make an invoice this payment is already allocated to disappear from the screen while its
   * allocation still counted toward the total, which reads as a bug in the arithmetic.
   *
   * Falls back to the parent's list until the fetch lands, so the matched rows are on screen
   * immediately rather than after a round trip.
   */
  const editableInvoices = useMemo(() => {
    const matchedIds = new Set(adjustItem?.matchedInvoices.map((match) => match.invoiceId) ?? []);
    let source = filteredOpenInvoices;
    if (assignedInvoices) {
      // Keyed by id so an invoice present in both lists is offered once. The ledger's copy wins,
      // being the fresher read of the balance.
      const byId = new Map(
        filteredOpenInvoices.filter((invoice) => matchedIds.has(invoice.id)).map((i) => [i.id, i])
      );
      assignedInvoices.forEach((invoice) => byId.set(invoice.id, invoice));
      source = [...byId.values()];
    }
    if (!invoiceSearch) {
      return source;
    }
    const search = invoiceSearch.toLowerCase();
    return source.filter(
      (invoice) =>
        invoice.docNumber?.toLowerCase().includes(search) ||
        invoice.customerName?.toLowerCase().includes(search) ||
        invoice.poNumber?.toLowerCase().includes(search)
    );
  }, [adjustItem?.matchedInvoices, assignedInvoices, filteredOpenInvoices, invoiceSearch]);

  /**
   * Why this payment cannot be posted, in one place.
   *
   * Both post buttons read it, so every queue that opens this modal inherits the same rule and none
   * of them can forget one. The server refuses both cases too — `describeUnpostableRecord` — and
   * disabling here is so the operator is told before they click rather than after a round trip.
   *
   * The customer comes first: it is the one the operator can fix from this modal.
   */
  const isConfirmed = adjustItem ? isRecordSettledForPosting(adjustItem) : false;

  /**
   * A customer picked but never applied, which must not be mistaken for one that was.
   *
   * The picker holds a selection now instead of committing it, and that staging is invisible to
   * everything below: saving or posting writes against the customer *on the record*, not the one on
   * screen. So choosing a new customer and then pressing Save applied the cash to the old one while
   * the new one sat selected in front of the operator — the screen saying one thing and the ledger
   * doing another, which is the precise shape of a payment landing on the wrong account.
   *
   * Blocking rather than silently applying or silently discarding: both of those decide on the
   * operator's behalf what an abandoned selection meant, and only they know. Apply it or cancel it.
   */
  const unappliedCustomerChange =
    changingCustomer && !!pendingBuyerId && pendingBuyerId !== currentBuyerId;

  const blockedReason = unappliedCustomerChange
    ? 'You have picked a customer but not applied it. Press Update customer to move this payment, or Cancel to keep it where it is.'
    : !hasBuyer
    ? 'Assign a Dill customer before posting — the ERP needs an account to credit.'
    : !isConfirmed
    ? 'The bank has not confirmed this payment yet. Link its deposit from Awaiting Bank before posting, so the cash is only applied once the money has arrived.'
    : null;

  return (
    <DillModal
      open={adjustItem !== null}
      onClose={onClose}
      title={title}
      subtitle={`Payment from ${adjustItem?.payerName ?? 'Unknown'} — ${formatCurrency(
        adjustItem?.amount ?? 0
      )}`}
      maxWidth="780px"
      footer={
        <>
          <DillButton onClick={onClose} label="Cancel" variant="ghost" />
          <DillButton
            onClick={onSaveOnly}
            label={saveLabel}
            // Primary where it is the only action, so the modal has an obvious next step.
            variant={onAdjustAndPost ? 'secondary' : 'primary'}
            icon={<FloppyDisk size={14} />}
            // Saving is blocked by an unapplied customer too, though it does not post: the
            // allocation would be stored against the customer the operator is in the middle of
            // moving away from, and read back later as a reviewed answer.
            isDisabled={!hasAllocations || unappliedCustomerChange}
          />
          {onPostWithoutApply && (
            <DillButton
              onClick={onPostWithoutApply}
              label="Post Unapplied"
              variant="secondary"
              icon={<ArrowSquareOut size={14} />}
              isDisabled={!!blockedReason}
            />
          )}
          {onAdjustAndPost && (
            <DillButton
              onClick={onAdjustAndPost}
              label={approveLabel}
              variant="primary"
              icon={<CheckCircle size={14} weight="bold" />}
              isDisabled={!isValidAllocation || !!blockedReason}
            />
          )}
        </>
      }>
      {adjustItem && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2.5">
          {/* Payment details grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-xs w-16 shrink-0">Date</span>
              <span className="font-medium text-gray-900">
                {formatDate(adjustItem.paymentDate)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-xs w-16 shrink-0">Channel</span>
              <ChannelBadge channel={adjustItem.channel} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-xs w-16 shrink-0">Created</span>
              <span className="text-xs text-gray-600">{formatDateTime(adjustItem.createdAt)}</span>
            </div>
            {adjustItem.checkNumber && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs w-16 shrink-0">Check #</span>
                <span className="font-medium text-gray-900">{adjustItem.checkNumber}</span>
              </div>
            )}
            {adjustItem.payerAccountNumber && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs w-16 shrink-0">Account</span>
                <span className="font-medium text-gray-900">{adjustItem.payerAccountNumber}</span>
              </div>
            )}
            {adjustItem.referenceNumbers.length > 0 && (
              <div className="flex items-center gap-2 col-span-2">
                <span className="text-gray-400 text-xs w-16 shrink-0">Refs</span>
                <div className="flex flex-wrap gap-1">
                  {adjustItem.referenceNumbers.map((ref, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                      {ref}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {adjustItem.memo && (
              <div className="flex items-start gap-2 col-span-2">
                <span className="text-gray-400 text-xs w-16 shrink-0">Memo</span>
                <span className="text-gray-700">{adjustItem.memo}</span>
              </div>
            )}
          </div>
          {/* Why the match scored what it did — this is the screen where that changes a decision */}
          {(adjustItem.matchConfidenceFactors?.length ?? 0) > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <div className="flex items-baseline gap-2 mb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Match confidence
                </span>
                <ConfidenceBadge confidence={adjustItem.matchConfidence} />
              </div>
              <ConfidenceFactorList factors={adjustItem.matchConfidenceFactors ?? []} />
            </div>
          )}
          {/*
            The one note a person writes. `memo` above is the payer's own wording and `matchNote`
            is the matcher's; this is the only one an operator owns, and the only one that leaves
            for the ERP — as PrivateNote, and only for a QuickBooks supplier. Labelled plainly
            rather than "Note for QuickBooks" because the other integrations ignore it, so naming
            one of them would be wrong for most suppliers.
          */}
          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Notes
              </span>
              {noteDraft.trim() !== savedNote.trim() && (
                <DillButton
                  onClick={handleSaveNote}
                  label={savingNote ? 'Saving...' : 'Save note'}
                  variant="secondary"
                  icon={<FloppyDisk size={12} />}
                  isDisabled={savingNote || isPosted}
                />
              )}
            </div>
            <textarea
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              disabled={isPosted}
              rows={2}
              maxLength={ERP_POSTING_NOTE_MAX_LENGTH}
              placeholder={
                isPosted ? 'This payment has posted; its note can no longer be changed' : 'Optional'
              }
              className="w-full px-2.5 py-1.5 text-xs text-gray-800 bg-white border border-gray-200 rounded-lg resize-y placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
          {/* File links */}
          {(adjustItem.sourceFile?.url || adjustItem.remittanceFile?.url) && (
            <div className="flex items-center gap-3 pt-1 border-t border-gray-200">
              {adjustItem.sourceFile?.url && (
                <a
                  href={adjustItem.sourceFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors">
                  <File size={12} />
                  {adjustItem.sourceFile.name || 'Source File'}
                </a>
              )}
              {adjustItem.remittanceFile?.url && (
                <a
                  href={adjustItem.remittanceFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors">
                  <File size={12} />
                  {adjustItem.remittanceFile.name || 'Remittance File'}
                </a>
              )}
            </div>
          )}
        </div>
      )}
      {adjustItem && (
        <div
          className={
            'mb-4 p-3 rounded-lg border ' +
            (hasBuyer ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200')
          }>
          <div className="flex items-center gap-3">
            <span className="text-gray-500 text-xs w-24 shrink-0">Dill customer</span>
            {hasBuyer && !changingCustomer ? (
              <>
                <span className="text-sm font-medium text-gray-900">{buyerLabel}</span>
                {/*
                    Offered only where the caller can clear the allocation, since changing the
                    customer without clearing it would leave this payment applied to the previous
                    customer's invoices.
                  */}
                {onAllocationsReset && (
                  <button
                    type="button"
                    onClick={handleStartChangeCustomer}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline bg-transparent">
                    Change
                  </button>
                )}
              </>
            ) : (
              <div className="flex items-center flex-1 gap-2">
                <div className="flex-1 max-w-sm">
                  {/* Searchable for the same reason AddRemittanceModal's picker is: this is every
                      customer the supplier has — hundreds of them — and finding one by eye down a
                      scroll list is slower than the rest of the correction put together. */}
                  <DillSelect
                    value={pendingBuyerId}
                    onChange={setPendingBuyerId}
                    options={customerOptions}
                    searchable
                    searchPlaceholder="Search customers..."
                    disabled={assigning || (loadingCustomers && customerOptions.length === 0)}
                    placeholder={
                      loadingCustomers && customerOptions.length === 0
                        ? 'Loading customers...'
                        : hasBuyer
                        ? `Currently ${buyerLabel} — pick a different customer`
                        : 'Select the customer this payment is from'
                    }
                  />
                </div>
                {/*
                    Submitting is its own act. The picker holds the choice so it can be read back
                    and corrected; nothing reaches the record until this is pressed.
                  */}
                <DillButton
                  onClick={handleSubmitCustomer}
                  label={assigning ? 'Saving...' : hasBuyer ? 'Update customer' : 'Assign customer'}
                  variant="primary"
                  isDisabled={!canSubmitCustomer}
                />
                {hasBuyer && (
                  <button
                    type="button"
                    onClick={handleCancelCustomerChange}
                    disabled={assigning}
                    className="text-xs text-gray-500 hover:text-gray-700 bg-transparent shrink-0">
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
          {/*
              No inline warning about the allocation here: the confirmation in
              `handleSubmitCustomer` says that at the moment it matters, and saying it twice for one
              action trains people to read neither.

              This one is different — it explains why the footer has just gone dead, and it belongs
              next to the control that caused it rather than in the banner below, which only renders
              for an unconfirmed payment and so would miss this entirely.
            */}
          {unappliedCustomerChange && (
            <p className="mt-2 text-xs text-amber-800">
              Not applied yet — saving and posting are paused until you press{' '}
              <span className="font-medium">Update customer</span> or{' '}
              <span className="font-medium">Cancel</span>.
            </p>
          )}
          {!hasBuyer && (
            <div className="flex items-start gap-2 mt-2">
              <UserPlus size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800 leading-relaxed">
                {adjustItem.payerName
                  ? `No customer matched the payer name "${adjustItem.payerName}". `
                  : 'This payment has no customer. '}
                {blockedReason}
              </p>
            </div>
          )}
        </div>
      )}
      {adjustItem && hasBuyer && !isConfirmed && (
        <div className="flex items-start gap-2 p-3 mb-4 border border-amber-200 rounded-lg bg-amber-50">
          <Clock size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-xs leading-relaxed text-amber-800">{blockedReason}</p>
        </div>
      )}
      {linkedDepositId && (
        <div className="mb-4 p-3 rounded-lg border border-emerald-200 bg-emerald-50">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-gray-500 text-xs w-24 shrink-0">Linked deposit</span>
              <span className="text-sm text-gray-900 truncate">
                {linkedDeposit ? (
                  <>
                    <span className="font-medium">{formatCurrency(linkedDeposit.amount)}</span>
                    <span className="text-gray-500">
                      {' '}
                      · {formatDate(linkedDeposit.date)}
                      {linkedDeposit.merchantName || linkedDeposit.name
                        ? ` · ${linkedDeposit.merchantName || linkedDeposit.name}`
                        : ''}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-500">A bank deposit is confirming this payment</span>
                )}
              </span>
            </div>
            {/* Undoing the link puts this payment back to awaiting confirmation. The deposit only
                returns to Unmatched Deposits if nothing else is left on it — the backend reverses
                the confidence boost and the bank date along with it. */}
            <button
              onClick={handleUnlinkDeposit}
              disabled={unlinking}
              className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-emerald-800 bg-white ring-1 ring-inset ring-emerald-600/30 hover:bg-emerald-100 disabled:opacity-60 transition-colors">
              <LinkBreak size={13} weight="bold" />
              {unlinking ? 'Unlinking...' : 'Unlink'}
            </button>
          </div>
          {/*
            Spelled out when the deposit is a bulk one, because otherwise its amount plainly
            exceeding this payment's reads as a mismatched link rather than a correct one.
          */}
          {(linkedDeposit?.matchedRemittanceIds?.length ?? 0) > 1 && (
            <p className="mt-2 ml-[7.5rem] text-xs text-emerald-800">
              This deposit was banked in bulk and confirms{' '}
              {linkedDeposit?.matchedRemittanceIds?.length} payments, so its amount is their total
              rather than this one&rsquo;s.
            </p>
          )}
        </div>
      )}
      {adjustItem?.matchNote && (
        <div className="mb-4 flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <Info size={16} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">{adjustItem.matchNote}</p>
        </div>
      )}
      {adjustItem && (
        <AllocationEditor
          invoices={editableInvoices}
          suggestedMatches={adjustItem.matchedInvoices}
          allocations={allocations}
          onAllocationChange={onAllocationChange}
          totalAllocated={totalAllocated}
          paymentAmount={adjustItem.amount}
          searchValue={invoiceSearch}
          onSearchChange={onInvoiceSearchChange}
          loading={loadingInvoices}
          onPostRemainderChange={onPostRemainderChange}
          postRemainderAsCredit={postRemainderAsCredit}
        />
      )}
    </DillModal>
  );
};
